import React, { useState, useEffect } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import axiosInstance from "../../utils/axiosInstance";

const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const recurrenceOptions = ["none", "daily", "weekly", "monthly"];

function AddDoctorScheduleForm({
  doctorId,
  setSchedule,
  setShowForm,
  setSuccessMessage,
  setIsError,
  onScheduleAdded,
}) {
  const [newSlot, setNewSlot] = useState({
    days: [],
    startTime: "",
    endTime: "",
    bookingType: "",
    slotInterval: "",
    availableTokens: "",
    recurringPattern: "",
    startDate: "",
    endDate: "",
    breaks: [],
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewSlot((prev) => ({ ...prev, [name]: value }));
  };

  const handleDayToggle = (day) => {
    setNewSlot((prev) => {
      const days = prev.days.includes(day)
        ? prev.days.filter((d) => d !== day)
        : [...prev.days, day];
      return { ...prev, days };
    });
  };

  const handleBreakChange = (index, field, value) => {
    const updatedBreaks = [...newSlot.breaks];
    updatedBreaks[index][field] = value;
    setNewSlot((prev) => ({ ...prev, breaks: updatedBreaks }));
  };

  const addBreak = () => {
    setNewSlot((prev) => ({
      ...prev,
      breaks: [...prev.breaks, { start: "", end: "" }],
    }));
  };

  const removeBreak = (index) => {
    const updatedBreaks = newSlot.breaks.filter((_, i) => i !== index);
    setNewSlot((prev) => ({ ...prev, breaks: updatedBreaks }));
  };

  const addScheduleSlot = async () => {
    const {
      days,
      startTime,
      endTime,
      bookingType,
      recurringPattern,
      startDate,
      endDate,
      availableTokens,
      slotInterval,
      breaks,
    } = newSlot;

    if (
      (recurringPattern !== "daily" &&
        recurringPattern !== "none" &&
        days.length === 0) ||
      !startTime ||
      !endTime ||
      !bookingType ||
      !recurringPattern ||
      !startDate ||
      (recurringPattern !== "none" && !endDate)
    ) {
      setSuccessMessage("Please fill all required fields.");
      setIsError(true);
      return;
    }

    const payload = {
      ...newSlot,
      days: days.join(","),
      breaks: breaks.filter((b) => b.start && b.end), // remove incomplete breaks
      availableTokens: bookingType === "token" ? Number(availableTokens) : null,
      slotInterval: bookingType === "interval" ? Number(slotInterval) : null,
    };

    try {
      const res = await axiosInstance.post(
        `/doctor/${doctorId}/schedules`,
        payload
      );

      setSchedule?.((prev) => [...prev, res.data]);

      setNewSlot({
        days: [],
        startTime: "",
        endTime: "",
        bookingType: "",
        slotInterval: "",
        availableTokens: "",
        recurringPattern: "",
        startDate: "",
        endDate: "",
        breaks: [],
      });

      setIsError(false);
      setSuccessMessage("Schedule added successfully!");
      setShowForm(false);

      onScheduleAdded?.();
    } catch (error) {
      console.error("Failed to add schedule:", error);
      setIsError(true);
      setSuccessMessage("❌ Failed to add schedule.");
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setSuccessMessage?.("");
      setIsError?.(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [setSuccessMessage, setIsError]);

  return (
    <div className="relative mb-6 bg-white p-4 rounded-lg shadow border space-y-4">
      <button
        onClick={() => setShowForm(false)}
        className="absolute top-2 right-2 text-gray-500 hover:text-black"
      >
        <X className="w-5 h-5" />
      </button>

      <h2 className="text-lg font-medium">Add New Schedule</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {/* Recurrence */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Recurrence Pattern
          </label>
          <select
            name="recurringPattern"
            value={newSlot.recurringPattern}
            onChange={handleInputChange}
            className="border p-2 rounded w-full"
          >
            <option value="">Select Recurrence</option>
            {recurrenceOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        {/* Start & End Dates */}
        <div>
          <label className="block text-sm font-medium mb-1">Start Date</label>
          <input
            type="date"
            name="startDate"
            value={newSlot.startDate}
            onChange={handleInputChange}
            className="border p-2 rounded w-full"
          />
        </div>

        {newSlot.recurringPattern !== "none" && (
          <div>
            <label className="block text-sm font-medium mb-1">End Date</label>
            <input
              type="date"
              name="endDate"
              value={newSlot.endDate}
              onChange={handleInputChange}
              className="border p-2 rounded w-full"
            />
          </div>
        )}

        {/* Booking Type */}
        <div>
          <label className="block text-sm font-medium mb-1">Booking Type</label>
          <select
            name="bookingType"
            value={newSlot.bookingType}
            onChange={handleInputChange}
            className="border p-2 rounded w-full"
          >
            <option value="">Select Booking Type</option>
            <option value="token">Token</option>
            <option value="interval">Interval</option>
          </select>
        </div>

        {/* Days */}
        {newSlot.recurringPattern !== "daily" &&
          newSlot.recurringPattern !== "none" && (
            <div>
              <label className="block text-sm font-medium mb-1">Days</label>
              <div className="flex flex-wrap gap-2">
                {daysOfWeek.map((day) => (
                  <label key={day} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={newSlot.days.includes(day)}
                      onChange={() => handleDayToggle(day)}
                    />
                    {day}
                  </label>
                ))}
              </div>
            </div>
          )}

        {/* Times */}
        <div>
          <label className="block text-sm font-medium mb-1">Start Time</label>
          <input
            type="time"
            name="startTime"
            value={newSlot.startTime}
            onChange={handleInputChange}
            className="border p-2 rounded w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">End Time</label>
          <input
            type="time"
            name="endTime"
            value={newSlot.endTime}
            onChange={handleInputChange}
            className="border p-2 rounded w-full"
          />
        </div>

        {/* Token / Interval inputs */}
        {newSlot.bookingType === "token" && (
          <div>
            <label className="block text-sm font-medium mb-1">
              Available Tokens
            </label>
            <input
              type="number"
              name="availableTokens"
              value={newSlot.availableTokens}
              onChange={handleInputChange}
              className="border p-2 rounded w-full"
            />
          </div>
        )}

        {newSlot.bookingType === "interval" && (
          <div>
            <label className="block text-sm font-medium mb-1">
              Slot Interval (minutes)
            </label>
            <input
              type="number"
              name="slotInterval"
              value={newSlot.slotInterval}
              onChange={handleInputChange}
              className="border p-2 rounded w-full"
            />
          </div>
        )}
      </div>

      {/* Break Time Section */}
      <div className="mt-4">
        <label className="block text-sm font-medium mb-2">Breaks</label>
        {newSlot.breaks.map((b, idx) => (
          <div key={idx} className="flex gap-2 mb-2 items-center">
            <input
              type="time"
              value={b.start}
              onChange={(e) => handleBreakChange(idx, "start", e.target.value)}
              className="border p-2 rounded"
            />
            <span>to</span>
            <input
              type="time"
              value={b.end}
              onChange={(e) => handleBreakChange(idx, "end", e.target.value)}
              className="border p-2 rounded"
            />
            <button
              type="button"
              onClick={() => removeBreak(idx)}
              className="text-red-500 hover:text-red-700"
            >
              <Trash2 size={18} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addBreak}
          className="mt-1 text-sm text-blue-600 hover:underline flex items-center gap-1"
        >
          <Plus size={16} /> Add Break
        </button>
      </div>

      <button
        onClick={addScheduleSlot}
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded mt-4"
      >
        Add Schedule
      </button>
    </div>
  );
}

export default AddDoctorScheduleForm;
